import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import reduxStore from "reduxstore/reduxStore";
import i18n from "i18n/i18n";
import axios from "axios";
import Uri from "jsuri";
import { AuthorizationToken } from "helpers/AuthorizationToken";
import {
  Button,
  Select,
  useConfirmModal,
  SearchSuggestionSelect
} from "@texada/ui";
import {
  TIS_SESSION_VERIFY_URL,
  USER_URL,
  COMPANY_PERMISSION_URL,
  USER_PERMISSION_URL,
  HEADOFFICE_OPTION_VALUE,
  APP_DEFAULT_URL
} from "constants/AppConstants";
import SRMSyncLoader from "./SRMSyncLoader";
import { ReduxState, isPlayEnv } from "helpers/helper";
import {
  LoginFullResponse,
  LoginSource,
  setDocDivisionAccess,
  setUser,
  openSrmManagement,
  setTokens,
  switchInstance,
  getLoginError,
  getLoginSource,
  getSelectedLoginTo
} from "./loginUtils";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import {
  setCurrentUser,
  generateNewPermissionData,
  generateOldPermissionData
} from "actions/authActions";

const preventDefault = (callback, loginSource) => e => {
  e.preventDefault();
  callback(loginSource);
};

const VerifyLoginUser = () => {
  const { companyId } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const user = useSelector<ReduxState, any>(state => state?.auth?.user);
  const isLoggedIn = useSelector<ReduxState, boolean[]>(
    state => state.auth.isAuthenticated
  );
  const [loginSource, setLoginSource] = useState(
    companyId ? LoginSource.TW : localStorage.getItem("loginSource")
  );
  const [search, setSearch] = useState("");
  const [selectedDivision, setSelectedDivision] = useState({});
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [errors, setErrors] = useState<{
    loginSource?: string;
    selectedDivision?: string;
    selectedLocation?: string;
  }>({});
  const [isLoading, setLoading] = useState(false);
  const [isSyncingData, setIsSyncingData] = useState(true);
  const [ConfirmModalLogin, showConfirmModal] = useConfirmModal();
  const [loginConfirmed, setLoginComfirmed] = useState(false);

  useEffect(() => {
    let tenant = null;
    // switch instance
    if (query.get("tenant")) {
      tenant = query.get("tenant");
    }

    if (user && localStorage.jwtToken) {
      if (query.get("callbackUrl")) {
        redirectToMobile(user);
        return;
      }

      if (query.get("applicationId")) {
        handleSubmit(getSelectedLoginTo(query.get("applicationId")), true);
        return;
      }

      if (user?.compperms?.operator_by_division) {
        let userDivisions = user?.divisions;
        if (user?.upgraded) {
          userDivisions = user?.divisions?.filter(e => e.is_user_division);
        }
        fetchDivisions(user, userDivisions);
        return;
      }
      setIsSyncingData(false);
      return;
    }

    if (!tenant) return;
    validateSession(tenant);
  }, []);

  const validateSession = async tenant => {
    let tokenRes;
    try {
      tokenRes = await axios.post(
        `${TIS_SESSION_VERIFY_URL}/${tenant}?is_login=true`,
        { source: getLoginSource(query.get("source")) }
      );
      await setTokens(tokenRes.data);
      AuthorizationToken.set(tokenRes.data.token);
      if (!tokenRes.data.company.upgraded) {
        setNonUpgradedUserData(tokenRes.data);
        return;
      }

      if (tokenRes.data.user?.userinstance?.current.id !== tenant) {
        await switchInstance(
          tokenRes.data.user.id,
          tokenRes.data.user?.userinstance?.current.id,
          tenant
        );
        // get new token that contains the new company id
        tokenRes = await axios.post(`${TIS_SESSION_VERIFY_URL}/${tenant}`, {
          source: getLoginSource(query.get("source"))
        });
        await setTokens(tokenRes.data);
        AuthorizationToken.set(tokenRes.data.token);
      }
      setUpgradedUserData(tokenRes.data);
    } catch (err) {
      if (!isLoggedIn) {
        if (query.get("callbackUrl")) {
          window.location.href = new Uri(query.get("callbackUrl"))
            .addQueryParam("error", getLoginError(err))
            .toString();
          return;
        }

        localStorage.setItem("login-error", getLoginError(err));
        window.location.href = window.location.origin;
      }
    }
  };

  const setUpgradedUserData = async userData => {
    const companyPermissionRes = await axios.get(
      `${COMPANY_PERMISSION_URL}${userData.user.company}`
    );
    const userRes = await axios.get(
      `${USER_URL}${userData.user.id}?include_flask=true`
    );
    const permissionRes = await axios.get(
      `${USER_PERMISSION_URL}${userData.user.id}`
    );
    const permissions = generateNewPermissionData(
      companyPermissionRes.data,
      permissionRes.data.divisions,
      permissionRes.data.locations
    );

    let userInfo = {
      ...userData,
      user: userRes.data,
      srm_uid: userRes.data.srm_uid,
      comp_perms: companyPermissionRes.data,
      permissions: permissions.permissions,
      locations: permissions.locations,
      divisions: permissions.divisions,
      useTexadaPay: companyPermissionRes.data.use_texada_pay,
      companyId: userData.user.company,
      upgraded: companyPermissionRes.data.upgraded,
      isSuperAdmin: permissionRes.data.is_super_admin,
      is_operator: userRes.data.is_support
    };
    userInfo = await setUser(userInfo as LoginFullResponse);

    if (query.get("callbackUrl")) {
      await redirectToMobile(userInfo);
      return;
    }

    if (query.get("applicationId")) {
      await handleSubmit(getSelectedLoginTo(query.get("applicationId")), true);
      return;
    }

    if (companyPermissionRes.data.operator_by_division) {
      fetchDivisions(
        userData.user,
        permissions?.divisions?.filter(e => e.is_user_division)
      );
      return;
    }

    setIsSyncingData(false);
  };

  const setNonUpgradedUserData = async userData => {
    const { user, comp_perms } = userData;
    const permissions = generateOldPermissionData(
      comp_perms,
      userData.divisions,
      userData.locations
    );

    let userInfo = {
      ...userData,
      isSuperAdmin: userData.user.is_super_admin,
      srm_uid: userData.user.srm_uid,
      permissions: {
        ...permissions.permissions,
        admin: user.settings_menu_access,
        update_settings: user.settings_menu_access,
        capture_signatures: user.capture_signatures,
        change_asset_status: user.change_asset_status,
        maintain_sites: user.maintain_sites,
        update_meter_reading: user.update_meter_reading,
        view_inventory: user.view_inventory,
        workorder_default_reporting: user.workorder_default_reporting
      },
      locations: permissions.locations,
      divisions: permissions.divisions,
      companyId: userData.user.company,
      upgraded: userData.company.upgraded
    };

    userInfo = await setUser(userInfo as LoginFullResponse);
    if (query.get("callbackUrl")) {
      await redirectToMobile(userInfo);
      return;
    }

    if (query.get("applicationId")) {
      await handleSubmit(getSelectedLoginTo(query.get("applicationId")), true);
      return;
    }

    if (comp_perms.operator_by_division) {
      fetchDivisions(userInfo, permissions?.divisions);
      return;
    }

    setIsSyncingData(false);
  };

  const fetchDivisions = async (user, divisions) => {
    setDivisionOptions(
      divisions.map(division => ({
        label: division.warehouse.title,
        value: division.warehouse.srm_uid || HEADOFFICE_OPTION_VALUE,
        srm_uid: division.warehouse.srm_uid
      }))
    );
    setSearch("");
    setSelectedDivision([]);
    setIsSyncingData(false);

    const docAccess = JSON.parse(localStorage.getItem("docDivisionAccess"));
    if (isEmpty(docAccess?.[user.company])) return;

    const docDivision = docAccess[user.company].divisions.find(
      e => e.is_doc_division
    );
    const target = user?.divisions?.find(
      e => e.warehouse.srm_uid === docDivision.srm_uid
    );

    if (!target) return;

    setSelectedDivision({
      srm_uid: target.warehouse.srm_uid,
      label: target.warehouse.title,
      value: target.warehouse.srm_uid || HEADOFFICE_OPTION_VALUE
    });
    setSearch(target.warehouse.title);
  };

  const validate = () => {
    const newErrors: {
      loginSource?: string;
      selectedDivision?: string;
      selectedLocation?: string;
    } = {};

    if (isEmpty(loginSource)) {
      newErrors.loginSource = i18n.t("field_is_required", {
        field: i18n.t("LoginSource")
      });
    }
    if (user?.compperms?.operator_by_division && isEmpty(selectedDivision)) {
      newErrors.selectedDivision = i18n.t("field_is_required", {
        field: i18n.t("division")
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (loginOption, skipValidation = false) => {
    const newErrors = validate();
    if (!isEmpty(newErrors) && !skipValidation) return;

    if (loginOption === LoginSource.SRM && !loginConfirmed && !skipValidation) {
      const shouldProceed = await showConfirmModal();
      setLoginComfirmed(shouldProceed);
      if (!shouldProceed) {
        return;
      }
    }

    localStorage.setItem("loginSource", loginOption);
    localStorage.setItem("verified", true);
    localStorage.setItem("currentTenant", query.get("tenant"));
    localStorage.removeItem("login-error");
    if (loginOption === LoginSource.SRM) {
      await openSrmManagement(
        user?.compperms?.operator_by_division,
        selectedDivision?.srm_uid
      );
      return;
    }

    try {
      setLoading(true);
      const userInfo = await setDocDivisionAccess(
        user,
        selectedDivision.srm_uid
      );
      await reduxStore.dispatch(setCurrentUser(userInfo));
      window.location.href = APP_DEFAULT_URL;
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const redirectToMobile = async user => {
    let mobileRedirectUrl = new Uri(query.get("callbackUrl")).addQueryParam(
      "token",
      localStorage.jwtToken
    );
    mobileRedirectUrl.addQueryParam("env", window.location.hostname);
    const userInfo = await setDocDivisionAccess(user);
    await reduxStore.dispatch(setCurrentUser(userInfo));
    localStorage.removeItem("mobileRedirectUrl");
    localStorage.setItem("verified", true);
    window.location.href = mobileRedirectUrl.toString();
  };

  if (!user || isSyncingData)
    return <SRMSyncLoader message={i18n.t("validating_user_please_wait")} />;

  return (
    <>
      <ConfirmModalLogin
        header={`${i18n.t("login_to")} ${i18n.t("texada_srm")}`}
        message={i18n.t(
          "by_choosing_login_to_texada_srm_this_page_will_redirect_to_srm_ui"
        )}
        confirmText={i18n.t("processed")}
        cancelText={i18n.t("cancel")}
      />
      <form
        className="login_form"
        onSubmit={e => preventDefault(handleSubmit, loginSource)(e)}
      >
        <Select
          label={i18n.t("login_to")}
          className={`password ${isPlayEnv() ? "play" : ""}`}
          required
          value={loginSource}
          options={[
            {
              label: i18n.t("texada_web"),
              value: LoginSource.TW
            },
            {
              label: i18n.t("texada_srm"),
              value: LoginSource.SRM
            }
          ]}
          onChange={e => {
            setLoginSource(e.target.value);
          }}
          error={errors?.loginSource}
        />

        {user?.compperms?.operator_by_division && (
          <SearchSuggestionSelect
            label={i18n.t("division")}
            required
            placeholder={i18n.t("select_document_division")}
            inputValue={search}
            onInputValueChange={setSearch}
            options={divisionOptions}
            selectedItem={selectedDivision?.value}
            onChange={setSelectedDivision}
            error={errors?.selectedDivision}
          />
        )}
        <Button
          className="login-button"
          isLoading={isLoading}
          buttonProps={{ type: "submit" }}
        >
          {i18n.t("login")}
        </Button>
      </form>
    </>
  );
};

export default VerifyLoginUser;
